import {computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import {getSettlementsSummaryData, SettlementsSummaryData} from '@/stores'
import {message} from 'antd'
import {injectable} from 'inversify'
import {LoadingState} from '@/types'
import {ITransactionsHistoryDataStore} from '@/pages/Overview'
import {PaginationContainerStoreType, TRangePickerContainerStore, TSelectContainerStore} from '@/components'
import {DEFAULT_PAGE_SIZE, SERVER_DATE_FORMAT} from '@/constants'

@injectable()
export class SettlementsTransactionsHistoryDataStore implements ITransactionsHistoryDataStore {
    page = 0
    size = 20
    _data: SettlementsSummaryData[] = []
    loadingState: LoadingState = LoadingState.IDLE
    rangePickerStore: TRangePickerContainerStore
    currencySelectStore: TSelectContainerStore
    paginationStore: PaginationContainerStoreType
    constructor(
        rangePickerStore: TRangePickerContainerStore,
        currencySelectStore: TSelectContainerStore,
        paginationStore: PaginationContainerStoreType
    ) {
        makeObservable(this, {
            loadingState: observable,
            data: computed,
            isLoading: computed
        })

        reaction(() => ({
            from: this.rangePickerStore.startDate,
            to: this.rangePickerStore.endDate
        }),
            () => {
                this.paginationStore.setPageNumber(1)
                this.paginationStore.setPageSize(DEFAULT_PAGE_SIZE)
            }
        )

        reaction(
            () => ({
                from: this.rangePickerStore.startDate,
                to: this.rangePickerStore.endDate,
                currency: this.currencySelectStore.value,
                page: this.paginationStore.pageNumber,
                size: this.paginationStore.pageSize
            }),
            ({from, to, currency, page, size }) => {
                if (from && to && currency && page && size) {
                    this.loadData()
                }
            }
        )

        this.rangePickerStore = rangePickerStore
        this.currencySelectStore = currencySelectStore
        this.paginationStore = paginationStore
        this.loadData()
    }

    get data() {
        if (
            this.loadingState !== LoadingState.LOADING &&
            this.loadingState !== LoadingState.FAILED &&
            this.loadingState !== LoadingState.DONE
        ) {
            this.loadData()
        }

        return this._data
    }

    get isLoading(): boolean {
        return this.loadingState === LoadingState.LOADING
    }

    public loadData = async() => {
        const request = {
            from: this.rangePickerStore.startDate.format(SERVER_DATE_FORMAT),
            to: this.rangePickerStore.endDate.format(SERVER_DATE_FORMAT),
            currency: this.currencySelectStore.value,
            page: this.paginationStore.pageNumber,
            size: this.paginationStore.pageSize
        }

        if (this.loadingState === LoadingState.LOADING) {
            return
        }

        runInAction(() => this.loadingState = LoadingState.LOADING)

        try {
            const {result, error} = await getSettlementsSummaryData(request)
            if (error) throw new Error(error.message)

            const {data, totalCount} = result
            runInAction(() => this._data = data)
            this.paginationStore.setTotal(totalCount)
        } catch (error) {
            message.error(error.message)
        } finally {
           runInAction(() => this.loadingState = LoadingState.DONE)
        }
    }
}
